import { Dialog } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/outline'
import { yupResolver } from '@hookform/resolvers/yup'
import { chunk, has, isEmpty, pick } from 'lodash'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useStep } from 'usehooks-ts'
import { API } from '../apis/API'
import { nextAPI } from '../apis/Axios'
import {
    contactInfoMailer,
    isValidAddress,
    mappingValidationRules,
    OptionalcontactInfoMailer,
    options,
    outputHeaders,
    postMapping,
    predefinedMappingFields,
    removeMailingDuplicates,
    removePropertyDuplicates,
    returnFields,
    SameAsMailiangAddresMatchingFields,
    SameAsMailingAddressFields,
    SameAsMailingAddressValue,
    shouldShow,
} from '../helpers/mapping'
import { CSV } from '../helpers/csvparse'
import {
    generateId,
    // isSmartyStreetLimitError,
    log,
    sleep,
} from '../helpers/utils'
import { ModalStore } from '../state/ModalStore'
import { InputRow } from './Inputs/InputRow'
import { ProgressBar } from './Progressbar'
import { parsePhoneNumber } from 'libphonenumber-js'
import WentWrong from './Modals/WentWrong'
import { XIcon } from '@heroicons/react/solid'
import Toggle from './Inputs/Toggle'
import * as Sentry from '@sentry/browser'
import ConfirmCSVRemove from './Modals/ConfirmCSVRemove'
import SampleReturnModal from './Modals/SampleReturnModal'
import { UserStore } from '../state/UserStore'
import LoginModal from './Modals/LoginModal'

// Fix Form - New Design
//  conditional validation of zip and state and city based on logic explained here
// https://www.notion.so/open-letter-marketing/Single-Product-Page-Notes-e2626364bffe4f17a35a90bb2a3d9e06#f72f989770e446e5b7e597d00a06df46

const RowHeader = () => {
    return (
        <div className="grid grid-cols-[30px_minmax(170px,1.1fr)_minmax(180px,1fr)_minmax(160px,1fr)] items-center gap-[10px] border-b-[1.5px] border-[#E1DACE] py-[9px] text-[11px] font-bold uppercase tracking-[0.04em] text-gray-500">
            <span />
            <span>OLM field</span>
            <span>Your column</span>
            <span>Preview</span>
        </div>
    )
}

const Section = ({ children }) => {
    return (
        <div className="mt-4 rounded-[13px] border border-[#ECE6DC] bg-[#FCFAF6] px-4 py-[14px] shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]">
            {children}
        </div>
    )
}

const SectionTitle = ({ children }) => {
    return (
        <div className="mb-[6px] flex items-center gap-[10px] text-[13px] font-bold text-[#1A1D23]">
            <span>{children}</span>
        </div>
    )
}

const ProcessCSVButton = ({ onClick }) => {
    return (
        <button
            data-button-id="process-csv-button"
            onClick={onClick}
            type="button"
            className="bg-olm text-white font-semibold relative flex items-center border border-olm rounded-lg p-2 text-center cursor-pointer hover:bg-olh hover:border-olh focus:outline-none focus:ring-1 focus:ring-olh focus:border-olh sm:text-sm"
            aria-haspopup="listbox"
            aria-expanded="true"
            aria-labelledby="listbox-label">
            <span className="mx-2">Process CSV</span>
            <CheckIcon className="h-6 w-6" />
        </button>
    )
}

const FooterButton = ({ onClick, children, variant = 'primary' }) => {
    const baseClassName =
        'inline-flex items-center justify-center rounded-full px-[22px] py-3 text-[14.5px] font-semibold transition-[transform,background-color,box-shadow] duration-150 active:translate-y-[1px] focus:outline-none'

    const variantClassName =
        variant === 'ghost'
            ? 'bg-[#EFE9E0] text-[#1A1D23] hover:bg-[#E7E0D5] focus-visible:ring-2 focus-visible:ring-[#E7E0D5] focus-visible:ring-offset-2'
            : 'bg-[linear-gradient(90deg,#ED5C2F,#F28230)] text-white shadow-[0_6px_16px_rgba(237,92,47,0.28)] hover:shadow-[0_10px_22px_rgba(237,92,47,0.34)] focus-visible:ring-4 focus-visible:ring-[#F28230]/15'

    return (
        <button
            onClick={onClick}
            type="button"
            className={`${baseClassName} ${variantClassName}`}>
            {children}
        </button>
    )
}

const HelperRow = ({ children }) => {
    return (
        <div className="mb-4">
            <p className="text-[13px] leading-[1.55] text-[#6B7280]">
                {children}
            </p>
        </div>
    )
}

const propertyFieldNames = ['propaddres', 'propcity', 'propst', 'propzip']
const mailingListTemplateUrl =
    'https://admin.openlettermarketing.com/wp-content/uploads/2023/11/OLM-Lead-Template.csv'

const isPropertyField = (item) => propertyFieldNames.includes(item?.name)

const savedProfileFieldsMap = {
    retname: 'return_name',
    retaddress: 'return_address',
    retarea: 'return_city_state_zip',
    signature: 'return_signature',
    retphone: 'return_phone',
    retemail: 'return_email_address',
    retwebsite: 'return_website',
}

const duplicateProfileCompareKeys = Object.values(savedProfileFieldsMap)

const collapseSpaces = (value) =>
    String(value || '')
        .trim()
        .replace(/\s+/g, ' ')

const normalizeProfileField = (key, value) => {
    const normalizedValue = collapseSpaces(value)

    if (key === 'return_phone') {
        if (!normalizedValue) return ''

        try {
            return parsePhoneNumber(normalizedValue, 'US').number
        } catch (error) {
            return normalizedValue.replace(/\D/g, '')
        }
    }

    if (key === 'return_email_address' || key === 'return_website') {
        return normalizedValue.toLowerCase()
    }

    return normalizedValue
}

export function Mapping({ file, onFinish }) {
    const [csv, setCSV] = useState([])
    const scrollContainerRef = useRef(null)

    const router = useRouter()
    const slug = router.query.slug

    const [mappingFields, setMappingFields] = useState([])
    const [fieldRowsOptions, setFieldRowsOptions] = useState(options)

    const [loading, setLoading] = useState(false)
    const [loadingPercent, setLoadingPercent] = useState(0)
    const [checkMailingDuplicate, setCheckMailingDuplicate] = useState(false)
    const [checkPropertyDuplicate, setCheckPropertyDuplicate] = useState(true)
    const [showPropertySection, setShowPropertySection] = useState(true)
    const [exampleRows, setExampleRows] = useState([])
    const [savedProfiles, setSavedProfiles] = useState([])
    const [selectedProfile, setSelectedProfile] = useState('')

    const [sampleModalShow, setSampleModalShow] = useState(false)

    const hasBeenForceClosed = useRef(false)
    const user = UserStore((state) => state.user)

    const totalSteps = 2
    const [currentStep, helpers] = useStep(totalSteps)
    const { canGoToNextStep, goToNextStep, goToPrevStep } = helpers

    const skimModal = ModalStore((state) => state.skim)
    const addModal = ModalStore((state) => state.add)

    const {
        handleSubmit,
        setValue: _setValue,
        formState: { errors },
        getValues,
    } = useForm({
        resolver: async function () {
            const values = arguments[0]
            return await yupResolver(
                mappingValidationRules({
                    values,
                    options: showPropertySection
                        ? fieldRowsOptions
                        : fieldRowsOptions.filter(
                              (field) => !isPropertyField(field),
                          ),
                    currentStep,
                    slug,
                }),
            )(...arguments)
        },
    })

    const setValue = (name, value) => {
        if (SameAsMailingAddressFields.includes(name)) {
            if (value === SameAsMailingAddressValue) {
                setFieldRowsOptions((fields) => {
                    const optionsToMatch = options
                        .slice(1, 4)
                        .map((option) => option.name)
                    return fields.filter(
                        (field) => !optionsToMatch.includes(field.name),
                    )
                })
            } else {
                setFieldRowsOptions(options)
            }
        }
        // return _setValue
        return _setValue(name, value)
    }

    const fetchSavedProfiles = async (userId = user?.customer?.id) => {
        try {
            if (!userId) {
                setSavedProfiles([])
                return []
            }

            const { data } = await nextAPI.post(
                `${process.env.NEXT_PUBLIC_WP_BASE_URL}/wp-json/olm-return-address-profile/v1/profiles`,
                {
                    user_id: userId,
                },
            )

            const profiles = Array.isArray(data?.marketing_profiles)
                ? data.marketing_profiles
                : []
            setSavedProfiles(profiles)
            return profiles
        } catch (error) {
            log('fetchSavedProfiles', error)
            setSavedProfiles([])
            return []
        }
    }

    const hasPropertyFieldsAvailable = fieldRowsOptions.some((field) => {
        if (!isPropertyField(field)) return false
        return shouldShow(field, slug)
    })

    useEffect(() => {
        if (!hasPropertyFieldsAvailable && showPropertySection) {
            setShowPropertySection(false)
        }
    }, [hasPropertyFieldsAvailable, showPropertySection])

    useEffect(() => {
        if (!showPropertySection) {
            propertyFieldNames.forEach((fieldName) => {
                _setValue(fieldName, null)
            })
        }
    }, [showPropertySection, _setValue])

    const getMappingFields = (item) => {
        if (SameAsMailingAddressFields.includes(item.name)) {
            return [SameAsMailingAddressValue, ...mappingFields]
        }
        return mappingFields
    }

    const profileLabel = (profile) => {
        const title = profile?.marketing_profile_name
        if (title) return title

        return [
            profile?.return_name,
            profile?.return_address,
            profile?.return_city_state_zip,
        ]
            .filter(Boolean)
            .join(' · ')
    }

    const loadProfile = (index) => {
        setSelectedProfile(index)
        if (index === '') return

        const profile = savedProfiles[Number(index)]
        if (!profile) return

        Object.entries(savedProfileFieldsMap).forEach(
            ([fieldName, metaKey]) => {
                const value = profile?.[metaKey]
                if (!isEmpty(value)) {
                    setValue(fieldName, value)
                }
            },
        )
    }

    const buildSenderProfile = (
        values,
        profileCount = savedProfiles.length,
    ) => {
        const senderProfile = Object.entries(savedProfileFieldsMap).reduce(
            (acc, [fieldName, metaKey]) => {
                acc[metaKey] = collapseSpaces(values?.[fieldName])
                return acc
            },
            {},
        )

        const addressLabel = [
            senderProfile.return_address,
            senderProfile.return_city_state_zip,
        ]
            .filter(Boolean)
            .join(' ')

        const generatedName = [
            senderProfile.return_name || 'No name',
            addressLabel || 'No address',
            senderProfile.return_phone || 'No phone',
            senderProfile.return_email_address || 'No email',
        ]

        senderProfile.marketing_profile_name = generatedName.length
            ? generatedName.join(' · ')
            : `Saved info ${profileCount + 1}`

        return senderProfile
    }

    const isDuplicateSenderProfile = (
        profile,
        existingProfiles = savedProfiles,
    ) => {
        return existingProfiles.some((existingProfile) =>
            duplicateProfileCompareKeys.every(
                (key) =>
                    normalizeProfileField(key, existingProfile?.[key]) ===
                    normalizeProfileField(key, profile?.[key]),
            ),
        )
    }

    const saveSenderProfileIfNeeded = async (values) => {
        const userId = user?.customer?.id
        if (!userId) return

        const currentProfile = buildSenderProfile(values)

        if (isDuplicateSenderProfile(currentProfile)) {
            const existingProfileIndex = savedProfiles.findIndex((profile) =>
                duplicateProfileCompareKeys.every(
                    (key) =>
                        normalizeProfileField(key, profile?.[key]) ===
                        normalizeProfileField(key, currentProfile?.[key]),
                ),
            )

            if (existingProfileIndex >= 0) {
                setSelectedProfile(String(existingProfileIndex))
            }
            return
        }

        const updatedProfiles = [
            ...savedProfiles,
            buildSenderProfile(values, savedProfiles.length),
        ]

        try {
            await nextAPI.post(
                `${process.env.NEXT_PUBLIC_WP_BASE_URL}/wp-json/olm-return-address-profile/v1/submit-profile`,
                {
                    user_id: userId,
                    data: updatedProfiles,
                },
            )

            setSavedProfiles(updatedProfiles)
            setSelectedProfile(String(updatedProfiles.length - 1))
        } catch (error) {
            log('saveSenderProfileIfNeeded', error)
        }
    }

    const handleExampleRows = (data) => {
        const exampleRows = data.slice(0, 2)
        setExampleRows(exampleRows)
    }

    const renameFieldsName = (fields) => {
        return fields.map((field) => renameFieldName(field))
    }

    const renameFieldName = (field) => {
        return field.replace(/county/i, 'county_OLM')
    }

    const handleCSV = (file) => {
        const complete = (data) => {
            data.meta.fields = renameFieldsName(data.meta.fields)
            setMappingFields([...predefinedMappingFields, ...data.meta.fields])
            data.data = data.data.map((item) => {
                let renamedRow = {}
                Object.keys(item).forEach((key) => {
                    renamedRow[renameFieldName(key)] = item[key]
                })
                renamedRow._id = generateId()
                return trimObject(renamedRow)
            })
            setCSV(data)
            handleExampleRows(data.data)
        }
        CSV.parse({ file, complete })
    }

    const trimObject = (obj) => {
        // return object as their each value is trimmed
        return Object.keys(obj).reduce((acc, key) => {
            log('trimObject', key)
            acc[key] = String(obj[key]).trim()
            return acc
        }, {})
    }

    const cleanZipCode = (zip) => {
        // this fixes list source file, as they have excel formula in zip code
        if (zip === undefined || zip === null) return ''
        return String(zip).replace('=', '').replaceAll('"', '').trim()
    }

    const hasText = (value) => !isEmpty(String(value || '').trim())

    const prepareMailingObject = ({
        base,
        row,
        mappedFields,
        mergedAddress,
    }) => {
        const zip = cleanZipCode(row[mappedFields['zip']])
        const city = row[mappedFields['city']]
        const state = row[mappedFields['st']]

        if (hasText(mergedAddress) && hasText(zip)) {
            return {
                ...base,
                street: mergedAddress,
                zipcode: zip,
            }
        }

        if (hasText(mergedAddress) && hasText(city) && hasText(state)) {
            return {
                ...base,
                street: mergedAddress,
                state,
                city,
            }
        }

        return {
            ...base,
            street: mergedAddress,
            zipcode: zip,
            state,
            city,
        }
    }

    const prepareDataForSmartyStreet = (chunk, mappedFields) => {
        /**
         * Logic for Property Address
         * https://www.notion.so/open-letter-marketing/Single-Product-Page-Notes-e2626364bffe4f17a35a90bb2a3d9e06#f72f989770e446e5b7e597d00a06df46
         * */

        let toSend = {
            mailingObject: [],
            propertyObject: [],
        }

        chunk.forEach((row) => {
            // @TODO Should we manage these fields in contants ?
            const base = {
                input_id: row._id,
                match: 'invalid',
            }

            let mergedAddress = row[mappedFields['address']]

            if (row[mappedFields['mailing_address_2']]) {
                mergedAddress =
                    mergedAddress + ' ' + row[mappedFields['mailing_address_2']]
            }

            const mailingObject = prepareMailingObject({
                base,
                row,
                mappedFields,
                mergedAddress,
            })

            // const propertyObject =
            //     mappedFields.propzip && mappedFields.propaddres
            //         ? {
            //               ...base,
            //               street: row[mappedFields['propaddres']],
            //               zipcode: row[mappedFields['propzip']],
            //           }
            //         : {
            //               ...base,
            //               street: row[mappedFields['propaddres']],
            //               state: row[mappedFields['propst']],
            //               city: row[mappedFields['propcity']],
            //           }

            const propertyObject = {
                ...base,
                propaddres: showPropertySection
                    ? row[mappedFields['propaddres']] || ''
                    : '',
                propcity: showPropertySection
                    ? row[mappedFields['propcity']] || ''
                    : '',
                propst: showPropertySection
                    ? row[mappedFields['propst']] || ''
                    : '',
                propzip:
                    showPropertySection && mappedFields['propzip']
                        ? cleanZipCode(row[mappedFields['propzip']])
                        : '',
            }

            toSend.mailingObject.push(mailingObject)
            toSend.propertyObject.push(propertyObject)
        })

        return toSend
    }

    const handleVerification = async (data) => {
        try {
            // console.log('[SmartyStreet][Client] sending verification chunk', {
            //     count: Array.isArray(data) ? data.length : 'non-array',
            //     summary: summarizeSmartyRequest(data),
            // })
            const { data: reponseData } = await API.getVerify(data)
            // console.log('[SmartyStreet][Client] received verification chunk', {
            //     requestCount: Array.isArray(data) ? data.length : 'non-array',
            //     responseCount: Array.isArray(reponseData)
            //         ? reponseData.length
            //         : 'non-array',
            //     missingComponents: countMissingComponents(reponseData),
            //     summary: summarizeSmartyResponse(reponseData),
            // })
            return reponseData
        } catch (e) {
            console.error('[SmartyStreet][Client] verification chunk failed', {
                message: e?.message,
                status: e?.response?.status,
                response: e?.response?.data,
                requestSummary: summarizeSmartyRequest(data),
            })
            // @NOTE
            // we will try again in all cases
            await sleep(3000)
            return handleVerification(data)
            // if (isSmartyStreetLimitError(e.response.data)) {
            //     await sleep(3000)
            //     return handleVerification(data)
            // } else {
            //     handleError(e)
            // }
        }
    }

    const handleSmartStreet = async (mappedFields) => {
        setLoadingPercent(0)

        const chunkSize = 100

        const toSendData = chunk(csv.data, chunkSize)
        const totalChunks = toSendData.length
        const chunkPercentage = 100 / totalChunks

        let smartyJson = []

        /**
         * @KNOWNISSUE
         * this loop does not stop if modal is closed
         * in middle of the process
         */

        const t0 = performance.now()
        for (const chunk of toSendData) {
            checkError()

            const fromSmarty = await verifyMailingAndPropertyAddress(
                chunk,
                mappedFields,
            )
            smartyJson.push(...fromSmarty)
            setLoadingPercent((state) => parseFloat(state) + chunkPercentage)
        }
        const t1 = performance.now()
        log(`Verification Took ${t1 - t0} milliseconds.`)

        return smartyJson
    }

    async function verifyMailingAndPropertyAddress(chunk, mappedFields) {
        const { mailingObject, propertyObject } = prepareDataForSmartyStreet(
            chunk,
            mappedFields,
        )

        const verifyMailing = await handleVerification(mailingObject)
        const verifyProperty = propertyObject //await handleVerification(propertyObject)

        let smartyJsonObject = []

        verifyMailing.forEach((item) => {
            if (!item) return

            const requestItem = mailingObject.find(
                (row) => row.input_id === item.input_id,
            )

            if (!item.components) {
                console.warn(
                    '[SmartyStreet][Client] response row missing components',
                    {
                        input_id: item.input_id,
                        dpv_match_code: item?.analysis?.dpv_match_code,
                        keys: Object.keys(item),
                        request: requestItem,
                    },
                )
            }

            const propertyItem = verifyProperty.find(
                (propItem) => propItem.input_id === item.input_id,
            )

            if (!propertyItem) return

            const { address, city, st, zip, dpv_match_code } =
                extractAddressDetailsFromSmartyJson(item, requestItem)

            // const {
            //     address: propaddres,
            //     city: propcity,
            //     st: propst,
            //     zip: propzip,
            // } = extractAddressDetailsFromSmartyJson(propertyItem)

            const { propaddres, propcity, propst, propzip } = propertyItem

            smartyJsonObject.push({
                input_id: item.input_id,
                dpv_match_code,
                address,
                city,
                st,
                zip,
                propaddres,
                propcity,
                propst,
                propzip,
            })
        })

        return smartyJsonObject
    }

    function extractAddressDetailsFromSmartyJson(smartyItem, requestItem = {}) {
        const components = smartyItem?.components || {}
        const address = smartyItem?.delivery_line_1 || requestItem?.street || ''
        const city = components?.city_name || requestItem?.city || ''
        const st = components?.state_abbreviation || requestItem?.state || ''
        const zipcode = components?.zipcode
        const plus4_code = components?.plus4_code
        const zip = zipcode
            ? plus4_code
                ? `${zipcode}-${plus4_code}`
                : zipcode
            : requestItem?.zipcode || ''
        const dpv_match_code = smartyItem?.analysis?.dpv_match_code

        return { address, city, st, zip, dpv_match_code }
    }

    function summarizeSmartyRequest(data) {
        if (!Array.isArray(data)) return null

        return {
            rowsWithStreet: data.filter((row) => Boolean(row?.street)).length,
            rowsWithZipcode: data.filter((row) => Boolean(row?.zipcode)).length,
            rowsWithCityState: data.filter((row) => row?.city && row?.state)
                .length,
            rowsWithBlankZipcode: data.filter(
                (row) => row && 'zipcode' in row && !row?.zipcode,
            ).length,
            sample: data.slice(0, 3).map((row) => ({
                input_id: row?.input_id,
                hasStreet: Boolean(row?.street),
                hasZipcode: Boolean(row?.zipcode),
                hasCity: Boolean(row?.city),
                hasState: Boolean(row?.state),
            })),
        }
    }

    function prepareRowWithHeadersData(item, mappedFields) {
        const row = csv.data.find(({ _id }) => _id === item.input_id)
        let rowObject = {}

        const format = (header, value) => {
            /**
             @TODO We need better phone input solution
            */
            // return value
            try {
                return header.includes('phone') && value
                    ? parsePhoneNumber(value, 'US').formatNational()
                    : value
            } catch (e) {
                return value
            }
        }

        // @NOTES
        // if a mapped field is not found in row, its a text based field
        // so we fill value from it directly
        // @TODO - Check if logic works for all products

        for (const header of outputHeaders) {
            rowObject[header] = has(row, [mappedFields[header]])
                ? format(header, row[mappedFields[header]])
                : format(header, mappedFields[header])

            if (header === 'zip') {
                // this is special column, and its just concat of others
                rowObject.citystzip = addCityStZipColumn(item)
            }

            // https://trello.com/c/luza3H7p/69-important-top-priority-adding-ref-internal-use-to-deliverable-lists
            // 23 Oct 2024
            if (header === 'propaddres') {
                // this is special column, and its first 3 chars of propaddres
                rowObject['ref-internal-use'] = addRefInternalUserColumn(item)
            }
        }

        rowObject = { ...rowObject, ...item } // add smarty street data
        const addtionalFields = addAdditionalFields(row, mappedFields)
        rowObject = { ...rowObject, ...addtionalFields }
        // https://trello.com/c/CZM0pUMr/65-deliverable-list-issue
        // to make sure its not taking citystzip from existing column of original csv
        rowObject.citystzip = addCityStZipColumn(item)
        rowObject['ref-internal-use'] = addRefInternalUserColumn(item)
        return rowObject
    }

    function addAdditionalFields(row, mappedFields) {
        const mappedFieldValues = [...Object.values(mappedFields), '_id']
        const csvRow = csv.data.find((_row) => row._id === _row._id)
        const restFields = Object.keys(csvRow).filter(
            (key) =>
                !mappedFieldValues.includes(key) &&
                key &&
                !outputHeaders.includes(key) &&
                !isEmpty(String(key).trim()),
        )
        const restOftheFieldsObject = pick(csvRow, restFields)
        return restOftheFieldsObject
    }

    const addCityStZipColumn = (item) => {
        let { city = '', st = '', zip = '' } = item
        zip = zip.split('-')[0]
        const citystzip = `${city}, ${st} ${zip}`
        return citystzip
    }

    const addRefInternalUserColumn = (item) => {
        let { propaddres } = item
        // lets get first 3 chars of propaddres
        const refInternalUser = String(propaddres).substring(0, 3)
        return refInternalUser
    }

    const addSecondHeader = (uniquePropertyAddress) => {
        let array = uniquePropertyAddress
        const header = uniquePropertyAddress[0]
        let secondHeader = {}
        Object.keys(header).forEach((key) => {
            secondHeader[key] = `<<${key}>>`
        })
        array.unshift(secondHeader)
        return array
    }

    const removeExtraHeaders = (array) => {
        return array.map((item) => {
            delete item.input_id
            delete item.dpv_match_code
            return item
        })
    }

    const generateMergedCSVFileJSON = (smartyJson, mappedFields) => {
        let invalids = []
        let valids = []

        // We need to remove duplicates by property address, which is madatory
        let uniquePropertyAddress =
            showPropertySection && checkPropertyDuplicate
                ? removePropertyDuplicates(smartyJson)
                : smartyJson

        // seperate
        uniquePropertyAddress.forEach((item) => {
            const itemWithHeaders = prepareRowWithHeadersData(
                item,
                mappedFields,
            )
            isValidAddress(item?.dpv_match_code)
                ? valids.push(itemWithHeaders)
                : invalids.push(itemWithHeaders)
        })

        if (checkMailingDuplicate) valids = removeMailingDuplicates(valids)

        if (valids.length < 1) {
            throw new Error('No valid addresses found.')
        }

        valids = valids.map((item, idx) => postMapping(item, idx))
        invalids = invalids.map((item, idx) => postMapping(item, idx))

        valids = addSecondHeader(valids)
        valids = removeExtraHeaders(valids)

        log(`Valid Addresses: ${valids.length}`)
        log(`Invalid Addresses: ${invalids.length}`)

        const originalTotalRows = csv.data.length

        // send back data
        onFinish({
            originalTotalRows,
            file,
            header: csv.meta.fields,
            data: {
                valids,
                invalids,
            },
            removedMailingDuplicates: checkMailingDuplicate,
            removedPropertyDuplicates: checkPropertyDuplicate,
        })
    }

    const handleSameAsMailingAddress = (mappedFields) => {
        if (!showPropertySection) return mappedFields

        const values = Object.values(mappedFields)
        if (values.includes(SameAsMailingAddressValue)) {
            for (const key of Object.keys(SameAsMailiangAddresMatchingFields)) {
                mappedFields[key] =
                    mappedFields[SameAsMailiangAddresMatchingFields[key]]
            }
        }
        return mappedFields
    }

    const handleFormSubmission = async (mappedFields) => {
        mappedFields = handleSameAsMailingAddress(mappedFields)
        try {
            setLoading(true)
            let smartyData = await handleSmartStreet(mappedFields)
            if (smartyData.length < 1) {
                throw Error('No Valid data found, Please try with another csv.')
            }
            checkError()
            setLoading(false)
            generateMergedCSVFileJSON(smartyData, mappedFields)
            await saveSenderProfileIfNeeded(mappedFields)
            skimModal()
        } catch (e) {
            handleError(e)
        }
    }

    const handleError = (e) => {
        log(e)
        Sentry.captureException(e)
        terminateProcess(e)
        // log(e)
        // if (e.name === 'TypeError') {
        //     terminateProcess()
        // }
    }

    const checkError = () => {
        if (hasBeenForceClosed.current) {
            throw new Error('FORCE_CLOSED')
        }
    }

    const terminateProcess = (e) => {
        skimModal()
        const msg =
            e?.response?.data?.message || e?.message || 'Something went wrong.'
        log(e?.response?.data)
        // if (e.name === 'TypeError') {
        addModal(<WentWrong title={'Something went wrong'} msg={msg} />)
        // }
    }

    const nextStep = () => {
        handleSubmit((data) => {
            canGoToNextStep ? goToNextStep() : handleFormSubmission(data)
        })()
    }

    const handleDelete = () => {
        hasBeenForceClosed.current = true
        skimModal() // Remove this modal
    }

    const deleteFile = () => {
        addModal(<ConfirmCSVRemove onConfirm={handleDelete} />)
    }

    const openLoginModal = () => {
        addModal(<LoginModal />)
    }

    useEffect(() => {
        handleCSV(file)
    }, [file])

    useEffect(() => {
        log('errors', errors)
    }, [errors])

    useEffect(() => {
        fetchSavedProfiles()
    }, [user?.customer?.id])

    useEffect(() => {
        if (!scrollContainerRef.current) return

        const frameId = window.requestAnimationFrame(() => {
            scrollContainerRef.current?.scrollTo({
                top: 0,
                behavior: 'auto',
            })
        })

        return () => window.cancelAnimationFrame(frameId)
    }, [currentStep])

    return (
        <>
            <Dialog
                open={true}
                onClose={deleteFile}
                className="fixed inset-0 z-[120] overflow-y-auto">
                <div
                    data-modal-id="mapping-modal"
                    className="flex min-h-screen items-start justify-center px-[18px] py-[34px]">
                    <Dialog.Overlay className="fixed inset-0 bg-[rgba(26,29,35,0.55)] backdrop-blur-[2px]" />

                    <div className="relative mx-auto w-full max-w-[920px] overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_rgba(26,29,35,0.18)]">
                        <Dialog.Title className="flex items-center justify-between bg-[linear-gradient(90deg,#ED5C2F,#F28230)] px-5 py-[15px] text-white">
                            <div className="min-w-0 flex-1 pr-4 text-left text-[15px] font-bold">
                                {file.path}
                            </div>
                            <button
                                onClick={deleteFile}
                                type="button"
                                className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-white/20 text-white transition hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/40">
                                <XIcon className="h-4 w-4" />
                            </button>
                        </Dialog.Title>

                        <div
                            ref={scrollContainerRef}
                            className="max-h-[70vh] overflow-y-auto px-[22px] pt-5 pb-5">
                            <div className="space-y-6">
                                {loading && (
                                    <div className="space-y-3 rounded-[13px] border border-[#ECE6DC] bg-[#FCFAF6] px-4 py-4">
                                        <p className="text-[13.5px] leading-[1.6] text-[#4A5160]">
                                            The system is validating addresses,
                                            and if selected, removing duplicate
                                            property and mailing addresses, and
                                            removing undeliverables.
                                        </p>
                                        <ProgressBar
                                            progressPercentage={loadingPercent}
                                        />
                                    </div>
                                )}
                                <div className={`${loading && 'hidden'}`}>
                                    {currentStep === 1 && (
                                        <>
                                            <div className="mb-[6px] flex items-center gap-3">
                                                <div className="text-[16px] font-extrabold text-[#1A1D23]">
                                                    Field mapping
                                                </div>
                                                <a
                                                    href={
                                                        mailingListTemplateUrl
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="ml-auto inline-flex items-center gap-[6px] rounded-[8px] border border-[#F6D9C2] px-3 py-[7px] text-[12.5px] font-medium transition-colors hover:bg-[#FDF4EC]">
                                                    <span>Free</span>
                                                    <span className="text-[#ED5C2F]">
                                                        Mailing List Template
                                                    </span>
                                                    <span> →</span>
                                                </a>
                                            </div>
                                            <HelperRow>
                                                Map the columns in your file to
                                                OLM&apos;s fields. We&apos;ve
                                                matched the obvious ones -
                                                adjust any that look off.
                                            </HelperRow>

                                            <Section>
                                                <SectionTitle>
                                                    Recipient &amp; mailing
                                                    address
                                                </SectionTitle>

                                                <RowHeader />

                                                {log(
                                                    'exampleRows',
                                                    exampleRows,
                                                )}

                                                {fieldRowsOptions &&
                                                    fieldRowsOptions.map(
                                                        (item, idx) => {
                                                            if (
                                                                isPropertyField(
                                                                    item,
                                                                )
                                                            ) {
                                                                return null
                                                            }
                                                            return (
                                                                shouldShow(
                                                                    item,
                                                                    slug,
                                                                ) && (
                                                                    <InputRow
                                                                        errors={
                                                                            errors
                                                                        }
                                                                        getValues={
                                                                            getValues
                                                                        }
                                                                        currentValue={getValues(
                                                                            item.name,
                                                                        )}
                                                                        setValue={
                                                                            setValue
                                                                        }
                                                                        mappingFields={getMappingFields(
                                                                            item,
                                                                        )}
                                                                        key={
                                                                            idx
                                                                        }
                                                                        item={
                                                                            item
                                                                        }
                                                                        exampleRows={
                                                                            exampleRows
                                                                        }
                                                                    />
                                                                )
                                                            )
                                                        },
                                                    )}

                                                <div className="mt-[14px] flex items-center gap-3 rounded-[10px] bg-[#FDF4EC] px-[14px] py-3">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-[13.5px] font-semibold text-[#1A1D23]">
                                                            Remove duplicate
                                                            mailing addresses
                                                        </div>
                                                        <div className="text-[11.5px] text-gray-500">
                                                            Drop repeat
                                                            recipients from your
                                                            mailing list
                                                        </div>
                                                    </div>
                                                    <Toggle
                                                        value={
                                                            checkMailingDuplicate
                                                        }
                                                        onChange={
                                                            setCheckMailingDuplicate
                                                        }
                                                    />
                                                </div>
                                            </Section>

                                            <Section>
                                                <div className="mb-[6px] flex items-center gap-[10px] text-[13px] font-bold text-[#1A1D23]">
                                                    <span>
                                                        Property address
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setShowPropertySection(
                                                                (value) =>
                                                                    !value,
                                                            )
                                                        }
                                                        className={`relative ml-auto inline-flex h-[26px] w-[46px] flex-shrink-0 items-center rounded-full p-[3px] transition-colors duration-200 ${
                                                            showPropertySection
                                                                ? 'bg-[linear-gradient(90deg,#ED5C2F,#F28230)]'
                                                                : 'bg-[#D9D2C6]'
                                                        }`}>
                                                        <span
                                                            aria-hidden="true"
                                                            className={`inline-block h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition duration-200 ease-in-out ${
                                                                showPropertySection
                                                                    ? 'translate-x-5'
                                                                    : 'translate-x-0'
                                                            }`}
                                                        />
                                                    </button>
                                                </div>

                                                {showPropertySection &&
                                                    hasPropertyFieldsAvailable && (
                                                        <>
                                                            <RowHeader />

                                                            {fieldRowsOptions &&
                                                                fieldRowsOptions.map(
                                                                    (
                                                                        item,
                                                                        idx,
                                                                    ) => {
                                                                        if (
                                                                            !isPropertyField(
                                                                                item,
                                                                            )
                                                                        ) {
                                                                            return null
                                                                        }
                                                                        return (
                                                                            shouldShow(
                                                                                item,
                                                                                slug,
                                                                            ) && (
                                                                                <InputRow
                                                                                    errors={
                                                                                        errors
                                                                                    }
                                                                                    getValues={
                                                                                        getValues
                                                                                    }
                                                                                    currentValue={getValues(
                                                                                        item.name,
                                                                                    )}
                                                                                    setValue={
                                                                                        setValue
                                                                                    }
                                                                                    mappingFields={getMappingFields(
                                                                                        item,
                                                                                    )}
                                                                                    key={`property-${idx}`}
                                                                                    item={
                                                                                        item
                                                                                    }
                                                                                    exampleRows={
                                                                                        exampleRows
                                                                                    }
                                                                                />
                                                                            )
                                                                        )
                                                                    },
                                                                )}

                                                            <div className="mt-[14px] flex items-center gap-3 rounded-[10px] bg-[#FDF4EC] px-[14px] py-3">
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="text-[13.5px] font-semibold text-[#1A1D23]">
                                                                        Remove
                                                                        duplicate
                                                                        property
                                                                        addresses
                                                                    </div>
                                                                    <div className="text-[11.5px] text-gray-500">
                                                                        Drop
                                                                        repeat
                                                                        properties
                                                                        from
                                                                        your
                                                                        mailing
                                                                        list
                                                                    </div>
                                                                </div>
                                                                <Toggle
                                                                    value={
                                                                        checkPropertyDuplicate
                                                                    }
                                                                    onChange={
                                                                        setCheckPropertyDuplicate
                                                                    }
                                                                />
                                                            </div>
                                                        </>
                                                    )}
                                            </Section>

                                            <p className="mt-[18px] text-[11px] leading-[1.5] text-gray-400">
                                                OLM will automatically remove
                                                blank and incomplete mailing
                                                addresses from your list.
                                            </p>
                                        </>
                                    )}
                                    {currentStep === 2 && (
                                        <>
                                            <div className="text-[16px] font-extrabold text-[#1A1D23]">
                                                Personalize your mailer
                                            </div>
                                            <HelperRow>
                                                Please map columns from your
                                                file to a field that matches OLM
                                                fields. Some fields may have
                                                already been mapped based on
                                                their existing names. You can
                                                map other fields manually to the
                                                correct destination field.”
                                            </HelperRow>

                                            {!user?.customer ? (
                                                <div className="mb-4 flex flex-col gap-3 rounded-[10px] border border-[#F6D9C2] bg-[#FFF8F0] px-4 py-3 text-[13px] leading-[1.55] text-[#6B7280] sm:flex-row sm:items-center sm:justify-between">
                                                    <span>
                                                        Log in to use saved
                                                        sender profiles.
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={openLoginModal}
                                                        className="inline-flex w-full items-center justify-center rounded-full bg-[linear-gradient(90deg,#ED5C2F,#F28230)] px-4 py-[11px] text-[13px] font-semibold text-white shadow-[0_6px_16px_rgba(237,92,47,0.24)] transition hover:shadow-[0_10px_22px_rgba(237,92,47,0.3)] sm:w-auto">
                                                        Log in
                                                    </button>
                                                </div>
                                            ) : savedProfiles.length > 0 ? (
                                                <div className="mb-4">
                                                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.04em] text-gray-500">
                                                        Use saved info
                                                    </label>
                                                    <div className="relative w-full">
                                                        <select
                                                            value={
                                                                selectedProfile
                                                            }
                                                            onChange={(event) =>
                                                                loadProfile(
                                                                    event.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="w-full appearance-none rounded-[10px] border border-[#E1DACE] bg-white px-3.5 py-[13px] pr-10 text-left text-[14.5px] font-medium text-[#1A1D23] transition-all duration-150 focus:border-[#F28230] focus:outline-none focus:ring-4 focus:ring-[#F28230]/15">
                                                            <option value="">
                                                                - Start fresh -
                                                            </option>
                                                            {savedProfiles.map(
                                                                (
                                                                    profile,
                                                                    index,
                                                                ) => (
                                                                    <option
                                                                        key={`${profile?.marketing_profile_name}-${index}`}
                                                                        value={
                                                                            index
                                                                        }>
                                                                        {profileLabel(
                                                                            profile,
                                                                        ) ||
                                                                            `Saved info ${
                                                                                index +
                                                                                1
                                                                            }`}
                                                                    </option>
                                                                ),
                                                            )}
                                                        </select>
                                                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-500">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-4 w-4"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                                strokeWidth="2">
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    d="M19 9l-7 7-7-7"
                                                                />
                                                            </svg>
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : null}

                                            <Section>
                                                <SectionTitle>
                                                    <span>Return Address </span>
                                                    <button
                                                        onClick={() =>
                                                            setSampleModalShow(
                                                                true,
                                                            )
                                                        }
                                                        className="text-xs ml-2 h-6 rounded-md px-4 bg-olm hover:bg-olh text-white uppercase font-bold">
                                                        Preview Mailer
                                                    </button>
                                                </SectionTitle>

                                                <SampleReturnModal
                                                    exampleRows={exampleRows}
                                                    getValues={getValues}
                                                    isOpen={sampleModalShow}
                                                    onClose={() =>
                                                        setSampleModalShow(
                                                            false,
                                                        )
                                                    }
                                                />

                                                <RowHeader />

                                                {returnFields.map(
                                                    (item, idx) => {
                                                        const show = item?.show
                                                            ? item.show.includes(
                                                                  slug,
                                                              )
                                                                ? true
                                                                : false
                                                            : true
                                                        return (
                                                            show && (
                                                                <InputRow
                                                                    errors={
                                                                        errors
                                                                    }
                                                                    getValues={
                                                                        getValues
                                                                    }
                                                                    currentValue={getValues(
                                                                        item.name,
                                                                    )}
                                                                    setValue={
                                                                        setValue
                                                                    }
                                                                    mappingFields={
                                                                        mappingFields
                                                                    }
                                                                    key={idx}
                                                                    item={item}
                                                                    exampleRows={
                                                                        exampleRows
                                                                    }
                                                                />
                                                            )
                                                        )
                                                    },
                                                )}
                                            </Section>

                                            <Section>
                                                <SectionTitle>
                                                    Contact Info on Mailer{' '}
                                                </SectionTitle>

                                                <RowHeader />

                                                {contactInfoMailer.map(
                                                    (item, idx) => {
                                                        const show = item?.show
                                                            ? item.show.includes(
                                                                  slug,
                                                              )
                                                                ? true
                                                                : false
                                                            : true
                                                        return (
                                                            show && (
                                                                <InputRow
                                                                    errors={
                                                                        errors
                                                                    }
                                                                    getValues={
                                                                        getValues
                                                                    }
                                                                    currentValue={getValues(
                                                                        item.name,
                                                                    )}
                                                                    setValue={
                                                                        setValue
                                                                    }
                                                                    mappingFields={
                                                                        mappingFields
                                                                    }
                                                                    key={idx}
                                                                    item={item}
                                                                    exampleRows={
                                                                        exampleRows
                                                                    }
                                                                />
                                                            )
                                                        )
                                                    },
                                                )}
                                            </Section>

                                            <Section>
                                                <SectionTitle>
                                                    <span>Optional: </span>
                                                    <br />
                                                    <span className="text-sm font-normal text-olm-gray">
                                                        If applicable, select
                                                        the fields to display on
                                                        your mailpiece.{' '}
                                                    </span>
                                                </SectionTitle>

                                                {OptionalcontactInfoMailer.map(
                                                    (item, idx) => {
                                                        const show = item?.show
                                                            ? item.show.includes(
                                                                  slug,
                                                              )
                                                                ? true
                                                                : false
                                                            : true
                                                        return (
                                                            show && (
                                                                <InputRow
                                                                    errors={
                                                                        errors
                                                                    }
                                                                    getValues={
                                                                        getValues
                                                                    }
                                                                    currentValue={getValues(
                                                                        item.name,
                                                                    )}
                                                                    setValue={
                                                                        setValue
                                                                    }
                                                                    mappingFields={
                                                                        mappingFields
                                                                    }
                                                                    key={idx}
                                                                    item={item}
                                                                    exampleRows={
                                                                        exampleRows
                                                                    }
                                                                />
                                                            )
                                                        )
                                                    },
                                                )}
                                            </Section>

                                            <p className="text-sm mt-6 text-gray-400">
                                                When{' '}
                                                <span className="text-primary">
                                                    Process CSV{' '}
                                                </span>{' '}
                                                is clicked, your data will be
                                                cleaned, validated and
                                                organized. This may take a few
                                                moments so we recommend that you
                                                double-check the information
                                                above.
                                            </p>
                                        </>
                                    )}

                                    <div className="sticky -bottom-5 z-10 -mx-[22px] mt-6 flex justify-between gap-3 border-t border-[#ECE6DC] bg-white px-[22px] py-4 shadow-[0_-8px_20px_rgba(26,29,35,0.08)]">
                                        {currentStep > 1 ? (
                                            <FooterButton
                                                onClick={goToPrevStep}
                                                variant="ghost">
                                                Back
                                            </FooterButton>
                                        ) : (
                                            <FooterButton
                                                onClick={deleteFile}
                                                variant="ghost">
                                                Cancel
                                            </FooterButton>
                                        )}
                                        {currentStep < totalSteps ? (
                                            <FooterButton onClick={nextStep}>
                                                Next
                                            </FooterButton>
                                        ) : (
                                            <ProcessCSVButton
                                                onClick={nextStep}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    )
}
